/**
 * Credentials the CRM may set but never read back. A Slash API key acts as the
 * Slash user who made it, with their full banking access: the browser only learns
 * that a key is saved, and its last four characters. A Shopify admin token opens
 * the merchant's whole shop, and an app secret mints such tokens.
 */
const WRITE_ONLY_CREDENTIAL_KEYS: Readonly<Record<string, readonly string[]>> = {
  slash: ['apiKey'],
  shopify: ['apiToken', 'shopifyAppClientSecret', 'apiSecretKey'],
}

/** What a write-only secret looks like once masked: `••••••••` and its last four characters. */
export const MASKED_SECRET_PREFIX = '••••••••'

export const isMaskedSecret = (value: unknown): boolean =>
  typeof value === 'string' && value.startsWith(MASKED_SECRET_PREFIX)

const maskSecret = (value: string): string => `${MASKED_SECRET_PREFIX}${value.slice(-4)}`

/** The integration as the CRM may see it: its write-only credentials masked. */
export const redactWriteOnlyCredentials = <T extends { provider: string; credentials: Record<string, any> | null }>(
  integration: T,
): T => {
  const keys = WRITE_ONLY_CREDENTIAL_KEYS[integration.provider]
  if (!keys || !integration.credentials) return integration

  const credentials = Object.fromEntries(
    Object.entries(integration.credentials).map(([key, value]) =>
      keys.includes(key) && typeof value === 'string' && value.length > 0 ? [key, maskSecret(value)] : [key, value],
    ),
  )
  return { ...integration, credentials }
}
